import type { TFunction } from 'i18next';
import type { ExtendedInitiative } from '../../../../../api/initiative-client/initiative-client.type';
import type { ProjectsResponse } from '../../../../../api/projects-client/projects-client.type';
import { formatCurrency } from '../../../../../helpers/currency-helpers';
import { formatInitiativeDate } from '../../../strategy.helpers';

/**
 * Get team information to display in the side section
 *
 * @param {ProjectsResponse} team - The team to get information for
 * @param {ExtendedInitiative | null} initiative - The initiative that the team belongs to
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {string} currencyCode - Currency code for cost formatting (default 'USD')
 * @returns {Array<{label: string, value: string | number | null | undefined}>} An array of label-value pairs with team information
 */
const getTeamInfo = (
  team: ProjectsResponse,
  initiative: ExtendedInitiative | null,
  t: TFunction,
  currencyCode = 'USD',
): { label: string; value: string | number | null | undefined }[] => {
  const initialContribution = initiative?.initial_contributions?.team[team.id];

  return [
    {
      label: t('strategy.initiative_performance.project_manager', 'PM'),
      value: team.pm,
    },
    {
      label: t('strategy.initiative_performance.cost_to_date_initiative', 'Cost to Date (This Initiative)'),
      value: formatCurrency(initiative?.costs_to_date?.teams[team.id], { currency: currencyCode }),
    },
    {
      label: t('strategy.initiative_performance.start_date_first_task', { work_unit: t('common.work_unit') }),
      value: initialContribution
        ? formatInitiativeDate(initialContribution, t)
        : t('strategy.initiatives.labels.no_data', '-'),
    },
    {
      label: t('strategy.initiative_performance.task_count_initiative', { work_unit: t('common.work_unit') }),
      value: team.task_count,
    },
  ];
};

export { getTeamInfo };
